import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  
  connectedCallback() {
    super.connectedCallback();
    
    // Add formdata handler for cart form submissions
    if (this.isDecimalMode) {
      const form = this.closest('form');
      if (form && !form.hasAttribute('data-decimal-handler-added')) {
        form.setAttribute('data-decimal-handler-added', 'true');
        
        this.boundCartFormDataHandler = (e) => {
          // Find all decimal mode inputs in this form
          const decimalInputs = form.querySelectorAll('cart-quantity-selector-component[data-decimal-mode="true"] input[name="updates[]"]');
          
          decimalInputs.forEach(input => {
            const displayValue = parseFloat(input.value) || 0.1;
            const actualValue = Math.round(displayValue * 10);
            const lineNumber = input.getAttribute('data-cart-line');
            
            console.log('Cart form submit - Line', lineNumber, 'Converting:', displayValue, '→', actualValue);
            
            // Update the FormData with actual values
            // The name is "updates[]" and we need to update the correct index
            const currentUpdates = e.formData.getAll('updates[]');
            const index = parseInt(lineNumber) - 1;
            
            if (index >= 0 && index < currentUpdates.length) {
              // Remove all existing updates
              e.formData.delete('updates[]');
              
              // Re-add them with the corrected value
              currentUpdates.forEach((value, i) => {
                if (i === index) {
                  e.formData.append('updates[]', actualValue.toString());
                } else {
                  e.formData.append('updates[]', value);
                }
              });
            }
          });
        };
        
        form.addEventListener('formdata', this.boundCartFormDataHandler, true);
      }
    }
  }
  
  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Remove cart form handler
    if (this.isDecimalMode && this.boundCartFormDataHandler) {
      const form = this.closest('form');
      if (form) {
        form.removeEventListener('formdata', this.boundCartFormDataHandler, true);
      }
    }
  }

  /**
   * Override initializeDecimalMode for cart items
   * The Liquid template already provides the correct display value,
   * so we just need to store the actual value
   */
  initializeDecimalMode() {
    const { quantityInput } = this.refs;
    
    // Keep as type="number" but set step to 0.1
    quantityInput.setAttribute('step', '0.1');
    quantityInput.setAttribute('min', '0.2');  // ← Changed to 0.2
    
    // The Liquid template already set the correct display value
    // We just need to read data-actual-value if it exists
    const actualValue = parseInt(quantityInput.getAttribute('data-actual-value')) || 2;  // ← Default to 2 (0.2m)
    
    console.log('Cart initializeDecimalMode - Display:', quantityInput.value, 'Actual:', actualValue);
    
    // Don't convert - value is already correct from Liquid template
    // Just make sure data-actual-value is set
    quantityInput.setAttribute('data-actual-value', actualValue.toString());
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}